// pages/creator-panel/Channels/WhatsappGroup/ListWhatsappGroups.js
//
// Groups list for the WhatsApp Group connector. Renders through the shared
// components/Common/DataTableWithFilters wrapper so it matches the /documents
// page (table chrome, filter row, right-aligned search, pagination, MUI action
// icons, selectable-rows bulk bar). Data is still server-paginated and filtered
// (page / per_page / search / group_id / status) — the wrapper's optional
// server-pagination props carry that through.
import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../../utils/axiosInstance";
import DataTableWithFilters from "../../../../components/Common/DataTableWithFilters";
import GroupMessagesModal from "./GroupMessagesModal";
import {
  Box, Typography, IconButton, Autocomplete, TextField, MenuItem,
  FormControl, InputLabel, Select, CircularProgress, Stack, Button, useTheme,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SyncIcon from "@mui/icons-material/Sync";
import SchoolIcon from "@mui/icons-material/School";
import DeleteIcon from "@mui/icons-material/Delete";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";

const sourceLabel = { export: "Export", qr: "QR", provider: "Provider" };

// Hoisted out of the list component so react-data-table-component keeps a stable
// reference (otherwise the expanded row remounts on every parent render).
const ExpandedGroupRow = ({ data }) => {
  const theme = useTheme();
  return (
    <Box p={2} sx={{ backgroundColor: theme.palette.action.hover }}>
      <Typography variant="body2" sx={{ mb: 1 }}><strong>Source:</strong> {sourceLabel[data.source] || data.source}</Typography>
      {data.group_external_id && (
        <Typography variant="body2" sx={{ mb: 1 }}><strong>Group ID:</strong> {data.group_external_id}</Typography>
      )}
      <Typography variant="body2" sx={{ mb: 1 }}><strong>Messages:</strong> {data.message_count || 0}</Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Characters:</strong> {data.page_characters || 0} · <strong>Words:</strong> {data.page_words || 0}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}><strong>Member numbers redacted:</strong> {data.anonymized ? "Yes" : "No"}</Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Last message:</strong> {data.last_message_at ? new Date(data.last_message_at).toLocaleString() : "N/A"}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Last synced:</strong> {data.last_sync_at ? new Date(data.last_sync_at).toLocaleString() : "Never"}
      </Typography>
      {data.summary && <Typography variant="body2"><strong>Summary:</strong> {data.summary}</Typography>}
    </Box>
  );
};

export default function ListWhatsappGroups({ refreshTrigger, permission, onNotify }) {
  const theme = useTheme();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  // Flips to push react-data-table-component back to page 1 after a filter change.
  const [resetPage, setResetPage] = useState(false);

  // Inline list-level feedback (Documents pattern: surfaced by the wrapper).
  const [listError, setListError] = useState("");
  const [listSuccess, setListSuccess] = useState("");

  // Data filters
  const [groupFilter, setGroupFilter] = useState(null);   // selected { id, group_name } | null
  const [statusFilter, setStatusFilter] = useState("");   // "" | "trained" | "not_trained"
  const [searchText, setSearchText] = useState("");       // free-text -> server `search`
  // Groups dropdown (server-backed typeahead) state
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupInput, setGroupInput] = useState("");
  const [optionsLoading, setOptionsLoading] = useState(false);

  // Selectable rows + bulk actions
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const { chatbotId } = useParams();
  const params = chatbotId ? { chatbot_id: chatbotId } : {};

  const note = (type, text) => { if (onNotify) onNotify(type, text); };
  const triggerReset = () => setResetPage((p) => !p);

  // Plain function (matches the codebase convention, e.g. ListDocuments.fetchDocuments);
  // every call passes the values it needs explicitly, so there is no stale-closure risk.
  const fetchGroups = async (
    page = currentPage,
    pageSize = perPage,
    gId = (groupFilter?.id || 0),
    status = statusFilter,
    search = searchText,
  ) => {
    setLoading(true);
    try {
      const qp = { ...params, page, per_page: pageSize };
      if (gId) qp.group_id = gId;
      if (status) qp.status = status;
      if (search) qp.search = search;
      const res = await axiosInstance.get("/api/whatsapp-group/groups", { params: qp });
      if (res.data.success) {
        setGroups(res.data.data);
        setTotalRows(res.data.pagination.total);
      }
    } catch (err) {
      setListError(err.response?.data?.message || "Error fetching groups.");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchGroups(); }, [refreshTrigger]);

  // Debounced free-text search -> server `search` param. Skips the initial mount
  // (the refreshTrigger effect already does the first fetch).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    const t = setTimeout(() => {
      setCurrentPage(1);
      triggerReset();
      fetchGroups(1, perPage, groupFilter?.id || 0, statusFilter, searchText);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // Populate the Groups dropdown via the same list endpoint (typeahead, debounced).
  // Server-backed so it keeps working past the first page of groups.
  useEffect(() => {
    let active = true;
    const t = setTimeout(async () => {
      setOptionsLoading(true);
      try {
        const res = await axiosInstance.get("/api/whatsapp-group/groups", {
          params: { ...params, page: 1, per_page: 25, search: groupInput },
        });
        if (active && res.data.success) {
          setGroupOptions(res.data.data.map((g) => ({ id: g.id, group_name: g.group_name })));
        }
      } catch (e) {
        /* non-fatal: the dropdown just shows no options */
      } finally {
        if (active) setOptionsLoading(false);
      }
    }, 350);
    return () => { active = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupInput, refreshTrigger]);

  const handlePageChange = (page) => { setCurrentPage(page); fetchGroups(page, perPage); };
  const handlePerRowsChange = (newPerPage, page) => { setPerPage(newPerPage); setCurrentPage(page); fetchGroups(page, newPerPage); };

  const handleGroupFilterChange = (val) => {
    setGroupFilter(val);
    setCurrentPage(1);
    triggerReset();
    fetchGroups(1, perPage, val?.id || 0, statusFilter, searchText);
  };

  const handleStatusChange = (val) => {
    setStatusFilter(val);
    setCurrentPage(1);
    triggerReset();
    fetchGroups(1, perPage, groupFilter?.id || 0, val, searchText);
  };

  const handleTrain = async (row) => {
    setBusyId(row.id);
    try {
      const res = await axiosInstance.post(`/api/whatsapp-group/groups/${row.id}/train`, {}, { params });
      if (res.data.success) setListSuccess(res.data.message || "Group trained.");
      else setListError(res.data.message || "Training failed.");
      fetchGroups();
    } catch (err) {
      setListError(err.response?.data?.message || "Training failed.");
    } finally {
      setBusyId(null);
    }
  };

  const handleSync = async (row) => {
    setBusyId(row.id);
    try {
      const res = await axiosInstance.post(`/api/whatsapp-group/groups/${row.id}/sync`, {}, { params });
      if (res.data.success) setListSuccess(res.data.message || "Group synced.");
      else setListError(res.data.message || "Sync failed.");
      fetchGroups();
    } catch (err) {
      setListError(err.response?.data?.message || "Sync failed.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete group "${row.group_name}"? This removes its trained data too.`)) return;
    setBusyId(row.id);
    try {
      await axiosInstance.delete(`/api/whatsapp-group/groups/${row.id}`, { params });
      setListSuccess("Group deleted successfully.");
      fetchGroups();
    } catch (err) {
      setListError(err.response?.data?.message || "Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  const handleSelectedRowsChange = ({ selectedRows: rows }) => setSelectedRows(rows);

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;
    if (!window.confirm(`Delete ${selectedRows.length} selected group(s)? This removes their trained data too.`)) return;
    setBulkBusy(true);
    try {
      // allSettled so a single failure doesn't hide the ones that succeeded.
      const results = await Promise.allSettled(selectedRows.map((g) =>
        axiosInstance.delete(`/api/whatsapp-group/groups/${g.id}`, { params })));
      const failed = results.filter((r) => r.status === "rejected").length;
      const ok = results.length - failed;
      if (failed > 0) setListError(`${failed} group(s) failed to delete${ok ? `; ${ok} deleted` : ""}.`);
      else setListSuccess(`${ok} group(s) deleted successfully.`);
      setSelectedRows([]);
      setToggleCleared((p) => !p);
      setCurrentPage(1);
      triggerReset();
      fetchGroups(1, perPage);
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkTrain = async () => {
    if (selectedRows.length === 0) return;
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(selectedRows.map((g) =>
        axiosInstance.post(`/api/whatsapp-group/groups/${g.id}/train`, {}, { params })));
      const failed = results.filter((r) => r.status === "rejected").length;
      const ok = results.length - failed;
      if (failed > 0) setListError(`${failed} group(s) failed to train${ok ? `; ${ok} trained` : ""}.`);
      else setListSuccess(`Training completed for ${ok} group(s).`);
      setSelectedRows([]);
      setToggleCleared((p) => !p);
      fetchGroups();
    } finally {
      setBulkBusy(false);
    }
  };

  const customDataTableStyles = {
    table: {
      style: { backgroundColor: theme.palette.background.paper },
    },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"}`,
      },
    },
    headCells: {
      style: { fontWeight: 600, fontSize: "14px", color: theme.palette.text.primary },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
      },
    },
    pagination: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderTop: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
      },
    },
    expanderRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
      },
    },
  };

  const columns = [
    {
      name: "Group",
      grow: 2,
      wrap: true,
      minWidth: "240px",
      cell: (row) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.group_name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {sourceLabel[row.source] || row.source} · {row.tokens || 0} tokens
          </Typography>
        </Box>
      ),
    },
    {
      name: "Messages",
      selector: (row) => row.message_count || 0,
      maxWidth: "120px",
    },
    {
      name: "Status",
      maxWidth: "150px",
      cell: (row) => (
        <Typography variant="body2" color={row.is_trained ? "primary" : "text.secondary"}>
          {row.is_trained ? "Trained" : "Not trained"}
        </Typography>
      ),
    },
    {
      name: "Action",
      width: "200px",
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      cell: (row) => (
        <Box display="flex" gap={1}>
          <IconButton color="primary" size="small" title="View messages"
            onClick={() => setSelectedGroup(row)} disabled={busyId === row.id}>
            <VisibilityIcon />
          </IconButton>
          {permission.canModify && row.source !== "export" && (
            <IconButton color="primary" size="small" title="Sync latest messages"
              onClick={() => handleSync(row)} disabled={busyId === row.id}>
              <SyncIcon />
            </IconButton>
          )}
          {permission.canModify && (
            <IconButton color="primary" size="small" title={row.is_trained ? "Retrain" : "Train"}
              onClick={() => handleTrain(row)} disabled={busyId === row.id}>
              <SchoolIcon />
            </IconButton>
          )}
          {permission.canDelete && (
            <IconButton color="error" size="small" title="Delete"
              onClick={() => handleDelete(row)} disabled={busyId === row.id}>
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  // Keep the selected group in the option list so MUI doesn't warn about an out-of-range value.
  const mergedGroupOptions = (groupFilter && !groupOptions.some((o) => o.id === groupFilter.id))
    ? [groupFilter, ...groupOptions]
    : groupOptions;

  const filterComponents = (
    <>
      {/* Groups — searchable server-backed typeahead */}
      <Autocomplete
        size="small"
        options={mergedGroupOptions}
        value={groupFilter}
        inputValue={groupInput}
        onChange={(e, val) => handleGroupFilterChange(val)}
        onInputChange={(e, val) => setGroupInput(val)}
        getOptionLabel={(o) => o.group_name || ""}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        loading={optionsLoading}
        noOptionsText="No groups"
        sx={{ minWidth: { xs: "100%", sm: 260 }, maxWidth: { xs: "100%", sm: 360 } }}
        renderInput={(p) => (
          <TextField
            {...p}
            label="Groups"
            placeholder="Search groups…"
            InputProps={{
              ...p.InputProps,
              endAdornment: (
                <>
                  {optionsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                  {p.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* Status */}
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="wag-status-label">Status</InputLabel>
        <Select
          labelId="wag-status-label"
          label="Status"
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
        >
          <MenuItem value="">All statuses</MenuItem>
          <MenuItem value="trained">Trained</MenuItem>
          <MenuItem value="not_trained">Not trained</MenuItem>
        </Select>
      </FormControl>
    </>
  );

  const selectedRowsActions = (
    <Stack direction="row" spacing={2}>
      {permission.canDelete && (
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={handleBulkDelete}
          startIcon={<DeleteIcon />}
          disabled={bulkBusy}
        >
          Bulk Delete
        </Button>
      )}
      {permission.canModify && (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleBulkTrain}
          disabled={bulkBusy}
          startIcon={bulkBusy ? <CircularProgress size={20} color="inherit" /> : <ModelTrainingIcon />}
        >
          {bulkBusy ? "Working..." : "Bulk Train"}
        </Button>
      )}
    </Stack>
  );

  return (
    <>
      <DataTableWithFilters
        columns={columns}
        data={groups}
        loading={loading}
        error={listError}
        onErrorClose={() => setListError("")}
        success={listSuccess}
        onSuccessClose={() => setListSuccess("")}
        filterComponents={filterComponents}
        selectedRowsActions={selectedRowsActions}
        selectedRowsCount={selectedRows.length}
        onSelectedRowsChange={handleSelectedRowsChange}
        clearSelectedRows={toggleCleared}
        expandableRows
        expandableRowsComponent={ExpandedGroupRow}
        noDataMessage="No groups yet. Import an export, or connect via Provider / QR above."
        theme={theme}
        customStyles={customDataTableStyles}
        searchText={searchText}
        onSearchChange={(e) => setSearchText(e.target.value)}
        searchPlaceholder="Search groups…"
        paginationServer
        paginationTotalRows={totalRows}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[10, 25, 50]}
        paginationResetDefaultPage={resetPage}
      />

      {selectedGroup && (
        <GroupMessagesModal
          group={selectedGroup}
          permission={permission}
          onNotify={note}
          onClose={(changed) => { setSelectedGroup(null); if (changed) fetchGroups(); }}
        />
      )}
    </>
  );
}
